import { provideAppInitializer, inject } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ContextService } from '../services/context.service';

/**
 * After Auth0 resolves, skip GET /me/context when the permissions cache is warm (TTL still valid).
 * That avoids Auth0/GW on every refresh. Missing/expired cache still fetches once.
 * Do not branch on clients or roles here — 401 vs no-group is handled by the interceptor and hasGroup.
 */
export function providePortalPermissionsBootstrapRefresh() {
  return provideAppInitializer(() => {
    const auth = inject(AuthService);
    const context = inject(ContextService);
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    // Callback loads context itself; avoid racing it (works with base href e.g. /client/callback).
    if (path.includes('/callback')) {
      return Promise.resolve();
    }
    return firstValueFrom(
      auth.isLoading$.pipe(
        filter(loading => !loading),
        take(1),
        switchMap(() => auth.isAuthenticated$.pipe(take(1))),
        switchMap(isAuthenticated => {
          if (!isAuthenticated) {
            return of(void 0);
          }
          const portal = context.getActivePortal();
          if (!portal) {
            return of(void 0);
          }
          if (context.getPermissions() !== null) {
            return of(void 0);
          }
          return context.loadUserContext(portal).pipe(
            tap(() => undefined),
            catchError(err => {
              console.warn('Portal permissions bootstrap refresh failed:', err);
              return of(void 0);
            })
          );
        })
      )
    );
  });
}
